import Column from '@app/components/layout/Column'
import { useTranslations } from '@i18n/useLanguage'
import { Tabs } from '@app/components/core/tabs/Tabs'
import { match } from 'ts-pattern'
import { useParamState } from '@app/util/hooks/useParamState'
import { HousingFormPageOverview } from './sections/HousingFormPageOverview'
import { HousingFormPagePlots } from './sections/HousingFormPagePlots'
import { HousingFormPageInitiatives } from './sections/HousingFormPageInitiatives'
import { housingFormTabs, housingFormTabsQueryParamName } from './constants'
import { Thread } from '@app/components/blocks/thread/Thread'
import { ViewHousingFormModel } from 'functions/app/housing-form/view'
import useMediaQuery from '@app/util/useMediaQuery'
import { HousingFormPagePopularHousingForms } from './sections/HousingFormPagePopularHousingForms'
import { KnowledgeHubModulesSection } from '@app/components/blocks/KnowledgeHubModulesSection'
import { FEATURE_TOGGLE_IDENTIFIERS } from '@schema/featureToggle'
import { isFeatureEnabled } from '@app/util/featureToggle'

type Tabs = keyof typeof housingFormTabs

const isKnowledgeHubTabAvailable = (
  housingForm: ViewHousingFormModel,
): boolean => {
  return (
    (housingForm.knowledgeHubModules?.length ?? 0) > 0 &&
    isFeatureEnabled(
      housingForm.featureToggles,
      FEATURE_TOGGLE_IDENTIFIERS.knowledgeHub,
    )
  )
}

export const TabContent = ({
  housingForm,
}: {
  housingForm: ViewHousingFormModel
}) => {
  const t = useTranslations().app.housingFormPage.tabs
  const isMobile = useMediaQuery()
  const showKnowledgeHubTab = isKnowledgeHubTabAvailable(housingForm)

  const [tab, setTab] = useParamState(
    housingFormTabs,
    housingFormTabs.overview,
    housingFormTabsQueryParamName,
  )

  return (
    <Column alignItems="stretch" gap="2rem" style={{ width: '100%' }}>
      <Tabs.Group selectedTab={tab}>
        <Tabs.Tab
          label={t.overview}
          tabKey={housingFormTabs.overview}
          onSelectTab={setTab}
        />
        <Tabs.Tab
          label={t.thread}
          tabKey={housingFormTabs.thread}
          onSelectTab={setTab}
        />
        <Tabs.Tab
          label={t.initiatives}
          tabKey={housingFormTabs.initiatives}
          onSelectTab={setTab}
        />
        <Tabs.Tab
          label={t.plots}
          tabKey={housingFormTabs.plots}
          onSelectTab={setTab}
        />
        {showKnowledgeHubTab && (
          <Tabs.Tab
            label={t.knowledgeHub}
            tabKey={housingFormTabs.knowledgeHub}
            onSelectTab={setTab}
          />
        )}
        {isMobile && (
          <Tabs.Tab
            label={t.popularHousingForms}
            tabKey={housingFormTabs.popularHousingForms}
            onSelectTab={setTab}
          />
        )}
      </Tabs.Group>

      {match(tab)
        .with(housingFormTabs.overview, () => (
          <HousingFormPageOverview housingForm={housingForm} />
        ))
        .with(housingFormTabs.thread, () =>
          housingForm.thread ? (
            <Thread key="thread" threadId={housingForm.thread.id} />
          ) : null,
        )
        .with(housingFormTabs.initiatives, () => (
          <HousingFormPageInitiatives housingForm={housingForm} />
        ))
        .with(housingFormTabs.plots, () => (
          <HousingFormPagePlots housingForm={housingForm} />
        ))
        .with(housingFormTabs.knowledgeHub, () =>
          showKnowledgeHubTab ? (
            <KnowledgeHubModulesSection
              modules={housingForm.knowledgeHubModules ?? []}
            />
          ) : null,
        )
        .with(housingFormTabs.popularHousingForms, () => (
          <HousingFormPagePopularHousingForms excludeIds={[housingForm.id]} />
        ))
        .exhaustive()}
    </Column>
  )
}
